import type { ViewClientSubscriptionDetails } from '../../api/viewClientApiService';
import { CLIENT_LABELS } from '../../constants/labels';

/** Formats an ISO date string to Polish locale (DD.MM.YYYY). Returns em-dash for null/invalid. */
function formatDate(date: string | null | undefined): string {
  if (!date) {
    return '—';
  }
  try {
    return new Date(date).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return date;
  }
}

/** Formats a numeric price string to two decimal places with "zł" suffix. */
function formatAmount(price: string | null | undefined): string {
  if (!price) {
    return '—';
  }
  const amount = parseFloat(price);
  if (isNaN(amount)) {
    return '—';
  }
  return `${amount.toFixed(2)} zł`;
}

/** Returns Tailwind badge classes and a label for the subscription plan status. */
function getSubStatusBadge(status: 'active' | 'inactive' | null | undefined) {
  if (status === 'active') {
    return {
      container: 'bg-success-90 text-success-30',
      dot: 'bg-success-40',
      label: CLIENT_LABELS.VIEW_CLIENT_SUB_STATUS_ACTIVE,
    };
  }
  return {
    container: 'bg-[#E5E7E9] text-[#878B8E]',
    dot: 'bg-[#878B8E]',
    label: CLIENT_LABELS.VIEW_CLIENT_SUB_STATUS_INACTIVE,
  };
}

/** Maps payment status codes ("paid" / "unpaid") to their Polish display labels. */
function getPaymentStatusLabel(status: string | null | undefined): string {
  if (status === 'paid') {
    return CLIENT_LABELS.VIEW_CLIENT_SUB_PAYMENT_PAID;
  }
  if (status === 'unpaid') {
    return CLIENT_LABELS.VIEW_CLIENT_SUB_PAYMENT_PENDING;
  }
  return '—';
}

/** Props for the ViewClientSubscription component. */
interface ViewClientSubscriptionProps {
  subscription: ViewClientSubscriptionDetails | null | undefined;
}

/**
 * Displays the "Subskrypcja" tab of the View Client page.
 *
 * Renders two cards:
 * - **Plan summary** — plan name with status badge, renewal date, price, and
 *   payment status. Shows an empty-state message when no plan is assigned.
 * - **Transaction history** — always empty for now; placeholder message shown.
 */
const ViewClientSubscription = ({
  subscription,
}: ViewClientSubscriptionProps) => {
  const badge = getSubStatusBadge(subscription?.planStatus);

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      {/* Subscription Summary */}
      <div className="flex flex-col gap-2">
        <p
          className="text-titleDefault font-medium text-on-surface-variant"
          data-testid="view-client-subscription-title"
        >
          {CLIENT_LABELS.VIEW_CLIENT_SUB_SUMMARY_TITLE}
        </p>
        <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
          {subscription?.planName ? (
            <div className="flex flex-col gap-4 p-4">
              {/* Plan name + status */}
              <div className="flex items-start gap-3 flex-wrap">
                <span
                  className="text-[16px] font-medium text-neutral-10 break-all min-w-0"
                  data-testid="view-client-plan-name"
                >
                  {subscription.planName}
                </span>
                <span
                  className={`shrink-0 px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${badge.container}`}
                  style={{ fontSize: '14px', lineHeight: '20px' }}
                >
                  <div
                    className={`rounded-full shrink-0 ${badge.dot}`}
                    style={{ width: '6px', height: '6px' }}
                  />
                  {badge.label}
                </span>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4">
                <div className="flex flex-col gap-1">
                  <p className="text-bodySmall font-normal text-on-surface-variant">
                    {CLIENT_LABELS.VIEW_CLIENT_SUB_RENEWAL_DATE}
                  </p>
                  <p
                    className="text-bodyLarge font-normal text-neutral-10"
                    data-testid="view-client-renewal-date"
                  >
                    {formatDate(subscription.nextRenewalDate)}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-bodySmall font-normal text-on-surface-variant">
                    {CLIENT_LABELS.VIEW_CLIENT_SUB_AMOUNT}
                  </p>
                  <p
                    className="text-bodyLarge font-normal text-neutral-10"
                    data-testid="view-client-amount"
                  >
                    {formatAmount(subscription.price)}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-bodySmall font-normal text-on-surface-variant">
                    {CLIENT_LABELS.FIELD_PAYMENT_STATUS}
                  </p>
                  <p
                    className="text-bodyLarge font-normal text-neutral-10"
                    data-testid="view-client-payment-status"
                  >
                    {getPaymentStatusLabel(subscription.paymentStatus)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-4">
              <p
                className="text-bodyMedium text-on-surface-variant"
                data-testid="view-client-subscription-empty"
              >
                {CLIENT_LABELS.VIEW_CLIENT_SUB_EMPTY}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Transaction History */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-titleDefault font-medium text-on-surface-variant"
          data-testid="view-client-transactions-title"
        >
          {CLIENT_LABELS.VIEW_CLIENT_SUB_TRANSACTIONS_TITLE}
        </p>
        <div className="flex items-center justify-center py-4">
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="view-client-transactions-empty"
          >
            {CLIENT_LABELS.VIEW_CLIENT_SUB_TRANSACTIONS_EMPTY}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ViewClientSubscription;
